import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

type FilterMode = 'all' | 'my' | 'favourites'
type FilterView = 'diagrams' | 'slides'

export const useFilterStore = defineStore('filter', () => {
  const mode = ref<FilterMode>('all')
  const view = ref<FilterView>('diagrams')
  const category = ref<string | number>('')
  const diagram = ref('')
  const slide = ref('')
  const tag = ref('')
  const ordering = ref('')
  const query = ref('')

  function setMode(m: FilterMode) {
    mode.value = m
    if (m === 'favourites') view.value = 'slides'
  }

  function toggleMode(m: FilterMode) {
    if (mode.value === m) {
      mode.value = 'all'
      if (m === 'favourites' || m === 'my') view.value = 'diagrams'
    } else {
      mode.value = m
      if (m === 'favourites') view.value = 'slides'
      if (m === 'my') view.value = 'slides'
    }
  }

  function setView(v: FilterView) {
    if (mode.value === 'favourites' && v === 'diagrams') return
    view.value = v
  }

  function setMy(flag: boolean) {
    mode.value = flag ? 'my' : 'all'
    if (flag) view.value = 'slides'
  }

  function setCategory(c: string | number) {
    category.value = c
    tag.value = ''
    diagram.value = ''
    slide.value = ''
  }

  function setTag(t: string) {
    tag.value = t
    category.value = ''
    diagram.value = ''
    slide.value = ''
  }

  function setDiagram(d: string | number) {
    diagram.value = `${d}`
    tag.value = ''
    category.value = ''
  }

  function setSlide(s: string | number) {
    slide.value = `${s}`
    tag.value = ''
    category.value = ''
  }

  function setOrdering(o: string) {
    ordering.value = o
  }

  function setQuery(q: string) {
    query.value = q
  }

  function removeFilter(type: string) {
    if (type === 'my' || type === 'favourites') {
      mode.value = 'all'
      view.value = 'diagrams'
    } else {
      if (type === 'category') category.value = ''
      else if (type === 'diagram') diagram.value = ''
      else if (type === 'slide') slide.value = ''
      else if (type === 'tag') tag.value = ''
      else if (type === 'ordering') ordering.value = ''
      else if (type === 'query') query.value = ''
    }
  }

  function clearFiltering() {
    mode.value = 'all'
    view.value = 'diagrams'
    category.value = ''
    diagram.value = ''
    slide.value = ''
    tag.value = ''
    ordering.value = ''
    query.value = ''
  }

  // Getters
  const filterKey = computed(() =>
    [mode.value, view.value, category.value, tag.value, diagram.value, slide.value, ordering.value, query.value].join('|')
  )

  function isActive(categoryId: string | number) {
    return category.value === categoryId
  }

  const filteringActive = computed(() =>
    mode.value !== 'all' || [category.value, diagram.value, slide.value, tag.value, ordering.value, query.value].some(v => v !== '')
  )

  const orderingLabel = computed(() => {
    const labels: Record<string, string> = { '-created_at': 'newest', '-popularity': 'popular' }
    return labels[ordering.value] ?? ''
  })

  const activeFilters = computed(() => {
    const orderingLabels: Record<string, string> = { '-created_at': 'Newest', '-popularity': 'Popular' }
    const filters: Array<{ label: string; value: any; type: string }> = []
    if (tag.value) filters.push({ label: tag.value, value: tag.value, type: 'tag' })
    if (category.value) filters.push({ value: category.value, label: String(category.value), type: 'category' })
    if (ordering.value) filters.push({ value: ordering.value, label: orderingLabels[ordering.value], type: 'ordering' })
    if (mode.value === 'my') filters.push({ value: true, label: view.value === 'diagrams' ? 'My decks' : 'My slides', type: 'my' })
    if (mode.value === 'favourites') filters.push({ value: true, label: 'Favourites', type: 'favourites' })
    if (diagram.value) filters.push({ value: diagram.value, label: 'diagram', type: 'diagram' })
    if (slide.value) filters.push({ value: slide.value, label: 'slide', type: 'slide' })
    if (query.value) filters.push({ value: query.value, label: query.value, type: 'query' })
    return filters
  })

  return {
    mode, view, category, diagram, slide, tag, ordering, query,
    setMode, toggleMode, setView, setMy, setCategory, setTag, setDiagram,
    setSlide, setOrdering, setQuery, removeFilter, clearFiltering,
    filterKey, isActive, filteringActive, orderingLabel, activeFilters
  }
})
